/**
 * Tiny `fetch` wrapper that adds the `Authorization: Bearer <accessToken>`
 * header to every API call. The bearer token is read at call time from a
 * caller-supplied accessor, so the wrapper composes with React state
 * updates without holding a stale reference.
 *
 * Validates: Requirements 8.1, 8.3
 */

/** Result of {@link apiFetch}: parsed body or error envelope. */
export interface ApiSuccess<T> {
  ok: true;
  status: number;
  data: T;
}
export interface ApiFailure {
  ok: false;
  status: number;
  error: string;
  message?: string;
  body?: unknown;
}
export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

/** Configuration for the API client. */
export interface ApiClientConfig {
  /** Base URL of the API Gateway stage. No trailing slash. */
  baseUrl: string;
  /** Returns the current access token, or `null` when signed out. */
  getAccessToken: () => string | null;
}

/** Options for a single request. */
export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  /** Extra query string parameters. Values are URI-encoded automatically. */
  query?: Record<string, string | number | undefined>;
  /** When `true`, the response is returned as text instead of JSON. */
  asText?: boolean;
  /** Allow callers to override the abort signal (e.g. on unmount). */
  signal?: AbortSignal;
}

/** Build a configured API client closure. */
export function createApiClient(config: ApiClientConfig) {
  return {
    request: <T>(path: string, options: ApiRequestOptions = {}): Promise<ApiResult<T>> =>
      apiFetch<T>(config, path, options),
  };
}

/**
 * Perform a single API call. Adds the `Authorization` header when an
 * access token is available; otherwise the request is sent unauthenticated
 * and the API will return 401 (Property 22).
 */
export async function apiFetch<T>(
  config: ApiClientConfig,
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResult<T>> {
  const url = buildUrl(config.baseUrl, path, options.query);
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  const token = config.getAccessToken();
  if (token !== null) headers['Authorization'] = `Bearer ${token}`;

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal !== undefined) init.signal = options.signal;

  const response = await fetch(url, init);
  if (response.status === 204) {
    return { ok: true, status: 204, data: undefined as unknown as T };
  }
  const contentType = response.headers.get('content-type') ?? '';
  if (options.asText === true) {
    const text = await response.text();
    if (!response.ok) {
      return { ok: false, status: response.status, error: 'http_error', message: text };
    }
    return { ok: true, status: response.status, data: text as unknown as T };
  }
  if (contentType.includes('application/json')) {
    const json = (await response.json()) as unknown;
    if (!response.ok) {
      const errBody = (json ?? {}) as { error?: string; message?: string };
      return {
        ok: false,
        status: response.status,
        error: errBody.error ?? 'http_error',
        ...(errBody.message !== undefined ? { message: errBody.message } : {}),
        body: json,
      };
    }
    return { ok: true, status: response.status, data: json as T };
  }
  // Non-JSON response — treat as failure for the typed callers.
  const text = await response.text();
  return { ok: false, status: response.status, error: 'unexpected_content_type', message: text };
}

/** Compose a URL with optional query params. Exported for tests. */
export function buildUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, string | number | undefined>,
): string {
  const trimmed = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const prefixedPath = path.startsWith('/') ? path : `/${path}`;
  if (query === undefined) return `${trimmed}${prefixedPath}`;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.set(k, String(v));
  }
  const qs = params.toString();
  if (qs.length === 0) return `${trimmed}${prefixedPath}`;
  return `${trimmed}${prefixedPath}?${qs}`;
}
